import { Component } from '@angular/core';
import {
  ActionSheetController,
  ModalController,
  PopoverController,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { ManageUsersService } from './services/manage-users.service';
import { filter, map, Observable, of, take, tap } from 'rxjs';
import {
  SortableSectionHeader,
  SortDirections,
} from '@shared/components/sortable-section/models/sortable-section.model';
import {
  HeaderName,
  MANAGE_USERS_HEADERS_DESKTOP,
  MANAGE_USERS_HEADERS_MOBILE,
  MANAGE_USERS_HEADERS_TABLET,
} from '@shared/constants/manage-users-constants';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { SimpleActionModalComponent } from '@shared/components/simple-action-modal/simple-action-modal.component';
import { CustomerUser } from './models/customer-user.model';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { Customer, Department } from '@usf/customer-types';
import { ManageUsersListSectionExpandedViewModalComponent } from './components/manage-users-list-section/manage-users-list-section-expanded-view-modal/manage-users-list-section-expanded-view-modal.component';
import { ModalWithNavComponent } from '@shared/components/modal-with-nav/modal-with-nav.component';
import { InviteUserModalComponent } from '@shared/components/invite-user-modal/invite-user-modal.component';
import { FEATURES } from '@shared/constants/splitio-features';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.page.html',
  styleUrls: ['./manage-users.page.scss'],
})
export class ManageUsersPage implements ViewWillEnter, ViewWillLeave {
  breadcrumbs: Crumb[];
  platformEnum = PlatformEnum;
  desktopHeaders: SortableSectionHeader[] = MANAGE_USERS_HEADERS_DESKTOP;
  tabletHeaders: SortableSectionHeader[] = MANAGE_USERS_HEADERS_TABLET;
  mobileHeaders: SortableSectionHeader[] = MANAGE_USERS_HEADERS_MOBILE;
  currentCustomer$: Observable<Customer>;
  currentDepartment$: Observable<Department>;

  customerUsers$ = this.manageUsersService.getCustomerUsers$(); //All CustomerUsers
  displayedUserList$: Observable<CustomerUser[]>; //Users filtered by search/sort
  customerUsersCallComplete$ =
    this.manageUsersService.isCustomerUsersCallComplete$();

  searchKey = '';
  selectedHeader: string;
  currentUserId: number = null; //for displaying ellipses for user actions

  private lastLoginMap: Map<string, Date> = new Map();
  noUsersMessage = 'i18n.manageUsers.noUsers';

  inviteUsersFeatureFlag$: Observable<boolean> = of(false);

  constructor(
    readonly platformService: PlatformService,
    private panAppState: PanAppState,
    private translateService: TranslateService,
    private manageUsersService: ManageUsersService,
    private actionSheetController: ActionSheetController,
    private popoverController: PopoverController,
    private modalService: ModalService,
    private modalController: ModalController,
    private loadingSpinnerService: LoadingSpinnerService,
  ) {}

  ionViewWillEnter() {
    this.inviteUsersFeatureFlag$ = this.panAppState.feature$([
      FEATURES.split_dynamic_manage_users_external_invite.name,
    ]);

    this.currentCustomer$ = this.manageUsersService.getCurrentCustomer$();
    this.currentDepartment$ = this.manageUsersService.getCurrentDepartment$();

    this.loadUsers();

    this.selectedHeader = HeaderName.USERNAME;

    this.displayedUserList$ = this.customerUsers$.pipe(
      filter(users => !!users && users?.length > 0),
      tap(users => {
        // Default sort is by username
        this.updateFilteredUserList({
          headerName: HeaderName.USERNAME,
          sortDirection: SortDirections.asc,
        });
      }),
    );

    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.manageUsers),
    );
    const homeCrumbUrl = this.platformService.isTouch.value
      ? '/touch/home'
      : '/desktop/home';

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: homeCrumbUrl,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.manageUsers',
        url: '/manage-users',
        active: true,
      },
    ];

    this.panAppState.user$
      .pipe(
        filter(user => !!user),
        take(1),
      )
      .subscribe(user => {
        this.currentUserId = user.ecomUserId;
      });
  }

  ionViewWillLeave(): void {
    //reset to get updated users everytime we land on page
    this.manageUsersService.setCustomerUsersCallComplete(false);
    this.manageUsersService.setCustomerUsers([]);
  }

  loadUsers(): void {
    this.loadingSpinnerService.createSpinnerModal().then(() => {
      this.manageUsersService.loadAllUsersForCustomer();
    });
  }

  // Handles search and sort for user list
  updateFilteredUserList(sortEvent?: any): void {
    const searchTerm = this.searchKey?.toLowerCase() || '';

    if (!!sortEvent?.sortDirection) {
      this.updateSortingCriteria(sortEvent);
    }

    const currentSortDirection = this.getCurrentSortDirection();
    const sortByLastLogin = this.selectedHeader === HeaderName.LAST_LOGIN;

    this.displayedUserList$ = this.customerUsers$.pipe(
      map(users => {
        const filteredUsers = this.filterUsersBySearch(users, searchTerm);
        const sortedUsers = this.sortUsers(
          filteredUsers,
          currentSortDirection,
          sortByLastLogin,
        );
        return sortedUsers;
      }),
    );
  }

  async openUserActionsHandler(user: CustomerUser) {
    const buttons = [];
    buttons.push(
      user?.admin === 'Y'
        ? {
            text: this.translateService.instant('i18n.manageUsers.removeAdmin'),
            handler: () => this.updateUserAdmin(user, false),
          }
        : {
            text: this.translateService.instant('i18n.manageUsers.makeAdmin'),
            handler: () => this.updateUserAdmin(user, true),
          },
      {
        text: this.translateService.instant('i18n.manageUsers.remove'),
        handler: () => this.removeUser(user),
      },
    );
    if (this.platformService.isTouch.value) {
      return this.openActionSheetTouch(user, buttons);
    } else {
      return this.openActionPopoverDesktop(user, buttons);
    }
  }

  async openActionSheetTouch(user: CustomerUser, buttons: any[]) {
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  async openUserExtendedView(user: CustomerUser) {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      ManageUsersListSectionExpandedViewModalComponent,
      {
        user,
      },
      'modal-desktop-size-md',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    return await modal.present();
  }

  async openActionPopoverDesktop(
    user: CustomerUser,
    buttons: any[],
  ): Promise<void> {
    const userOptionsPopover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      event,
      componentProps: {
        buttons,
      },
      cssClass: 'ellipsis-options-popover-desktop',
      mode: 'ios',
      showBackdrop: false,
    });
    await userOptionsPopover.present();
  }

  async updateUserAdmin(
    user: CustomerUser,
    isCustomerAdmin: boolean,
  ): Promise<void> {
    this.dismissUserOptions();
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      SimpleActionModalComponent,
      {
        withIcon: false,
        title: isCustomerAdmin
          ? 'i18n.manageUsers.addAdminAreYouSure'
          : 'i18n.manageUsers.removeAdminAreYouSure',
        content: isCustomerAdmin
          ? 'i18n.manageUsers.addAdminWarning'
          : 'i18n.manageUsers.removeAdminWarning',
        leftButtonText: 'i18n.common.cancel',
        rightButtonText: isCustomerAdmin
          ? 'i18n.manageUsers.makeAdmin'
          : 'i18n.manageUsers.removeAdmin',
      },
      'simple-action-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.dispatchAction) {
        this.loadingSpinnerService.createSpinnerModal().then(() => {
          this.manageUsersService.updateUserAdmin(user, isCustomerAdmin);
        });
      }
    });
    return await modal.present();
  }

  async removeUser(user: CustomerUser): Promise<void> {
    this.dismissUserOptions();

    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      SimpleActionModalComponent,
      {
        withIcon: false,
        title: 'i18n.manageUsers.areYouSure',
        content: 'i18n.manageUsers.removeUserWarning',
        leftButtonText: 'i18n.common.cancel',
        rightButtonText: 'i18n.manageUsers.removeUser',
      },
      'simple-action-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then(resp => {
      if (resp?.data?.dispatchAction) {
        this.loadingSpinnerService.createSpinnerModal().then(() => {
          this.manageUsersService.removeUser(user);
        });
      }
    });
    return await modal.present();
  }

  async dismissUserOptions() {
    if (this.platformService.isTouch.value) {
      await this.actionSheetController.dismiss();
    } else {
      await this.popoverController.dismiss();
    }
  }

  private filterUsersBySearch(users: any[], searchTerm: string): any[] {
    return users.filter(
      user =>
        (user?.email ?? '').toLowerCase().includes(searchTerm) ||
        (user?.firstName ?? '').toLowerCase().includes(searchTerm) ||
        (user?.lastName ?? '').toLowerCase().includes(searchTerm) ||
        (user?.userName ?? '').toLowerCase().includes(searchTerm),
    );
  }

  private sortUsers(
    users: CustomerUser[],
    sortDirection: string,
    sortByLastLogin: boolean,
  ): CustomerUser[] {
    if (sortByLastLogin && this.lastLoginMap.size === 0) {
      users.forEach(user => {
        //initialize last login map on first sort. This is to avoid re-parsing dates each sort.
        this.lastLoginMap.set(user.userName, this.parseDate(user.lastLogin));
      });
    }
    return [...users].sort((user1, user2) => {
      let valueA: any = user1[this.selectedHeader];
      let valueB: any = user2[this.selectedHeader];

      if (sortByLastLogin) {
        valueA = this.lastLoginMap.get(user1.userName);
        valueB = this.lastLoginMap.get(user2.userName);
      }

      if (valueA < valueB) {
        return sortDirection === SortDirections.asc ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortDirection === SortDirections.asc ? 1 : -1;
      }
      return 0;
    });
  }

  private getCurrentSortDirection(): string {
    return this.mobileHeaders
      .concat(this.desktopHeaders)
      .find(header => header.headerName === this.selectedHeader)?.sortDirection;
  }

  private updateSortingCriteria(sortEvent: {
    headerName: string;
    sortDirection: SortDirections;
  }): void {
    this.selectedHeader = sortEvent.headerName;

    this.mobileHeaders.concat(this.desktopHeaders).forEach(header => {
      if (header?.headerName === sortEvent?.headerName) {
        header.sortDirection = sortEvent.sortDirection;
      }
    });
  }

  private parseDate(value: Date | string): Date {
    const date = new Date(value);
    return isNaN(date.getTime()) ? new Date(0) : date;
  }

  trackByUserId(index: number, user: any): string {
    return user.userId;
  }

  async openInviteNewUserModal() {
    this.modalService.setModalOptions(
      this.platformService.platformType !==
        this.platformService.platformEnum.desktop,
      ModalWithNavComponent,
      {
        rootPage: InviteUserModalComponent,
      },
      'invite-new-user-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }
}
